<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MetaLeadFormsAPI from 'dashboard/api/metaLeadForms';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  forms: { type: Array, default: () => [] },
});

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const PERIODS = ['7', '30', '90'];
const EMPTY = '-';

const selectedId = ref(null);
const period = ref('30');
const loading = ref(false);
const exporting = ref(false);
const totals = ref({});
const byDay = ref([]);
const leads = ref([]);
const search = ref('');
const statusFilter = ref('all');
const expandedId = ref(null);

const hasForms = computed(() => props.forms.length > 0);

const sinceIso = () =>
  new Date(Date.now() - Number(period.value) * 24 * 60 * 60 * 1000).toISOString();

const load = async () => {
  if (!selectedId.value) return;
  loading.value = true;
  try {
    const { data } = await MetaLeadFormsAPI.leads(selectedId.value, {
      since: sinceIso(),
    });
    totals.value = data?.totals || {};
    byDay.value = Array.isArray(data?.by_day) ? data.by_day : [];
    leads.value = Array.isArray(data?.leads) ? data.leads : [];
  } catch (e) {
    totals.value = {};
    byDay.value = [];
    leads.value = [];
    useAlert(t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  if (hasForms.value) selectedId.value = props.forms[0].id;
});
watch(
  () => props.forms,
  list => {
    if (!selectedId.value && list.length) selectedId.value = list[0].id;
  }
);
watch([selectedId, period], load);

// ---- derivados ----
const filteredLeads = computed(() => {
  const q = search.value.trim().toLowerCase();
  return leads.value.filter(l => {
    if (statusFilter.value === 'qualified' && l.qualified !== true) return false;
    if (statusFilter.value === 'disqualified' && l.qualified !== false) return false;
    if (statusFilter.value === 'pending' && l.qualified !== null) return false;
    if (statusFilter.value === 'responded' && !l.responded) return false;
    if (!q) return true;
    return (
      (l.name || '').toLowerCase().includes(q) ||
      (l.phone || '').toLowerCase().includes(q) ||
      (l.email || '').toLowerCase().includes(q)
    );
  });
});

const pct = v => `${Math.round((Number(v) || 0) * 100)}%`;

// donut (qualificado / desqualificado / pendente)
const R = 52;
const C = 2 * Math.PI * R;
const donut = computed(() => {
  const t = totals.value;
  const total = t.total || 0;
  if (!total) return [];
  const defs = [
    { val: t.qualified || 0, color: '#22c55e' },
    { val: t.disqualified || 0, color: '#ef4444' },
    { val: t.pending || 0, color: '#94a3b8' },
  ];
  let acc = 0;
  return defs
    .filter(d => d.val > 0)
    .map(d => {
      const len = (d.val / total) * C;
      const seg = { color: d.color, dash: `${len} ${C - len}`, offset: -acc };
      acc += len;
      return seg;
    });
});

const maxDay = computed(() =>
  Math.max(1, ...byDay.value.map(d => d.total || 0))
);

const formatDay = iso => {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

const qualBadge = q => {
  if (q === true)
    return { label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.BADGE_QUALIFIED'), cls: 'bg-n-teal-3 text-n-teal-11' };
  if (q === false)
    return {
      label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.BADGE_DISQUALIFIED'),
      cls: 'bg-n-ruby-3 text-n-ruby-11',
    };
  return { label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.BADGE_PENDING'), cls: 'bg-n-alpha-2 text-n-slate-11' };
};

const STATUS_OPTIONS = computed(() => [
  { value: 'all', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.ALL') },
  { value: 'qualified', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.QUALIFIED') },
  { value: 'disqualified', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.DISQUALIFIED') },
  { value: 'pending', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.PENDING') },
  { value: 'responded', label: t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.REPLIED') },
]);

const toggle = id => {
  expandedId.value = expandedId.value === id ? null : id;
};

const openConversation = lead => {
  const displayId = lead?.conversation?.display_id;
  if (!displayId) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.NO_CONVERSATION'));
    return;
  }
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: displayId,
    },
  });
};

const exportCsv = async () => {
  if (!selectedId.value) return;
  exporting.value = true;
  try {
    const { data } = await MetaLeadFormsAPI.leadsCsv(selectedId.value, {
      since: sinceIso(),
    });
    const url = window.URL.createObjectURL(
      new Blob([data], { type: 'text/csv;charset=utf-8;' })
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${selectedId.value}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.EXPORT_ERROR'));
  } finally {
    exporting.value = false;
  }
};

const answerEntries = lead => Object.entries(lead.answers || {});
</script>

<template>
  <section class="flex flex-col gap-3">
    <div class="flex items-center justify-between gap-2 flex-wrap">
      <h2 class="text-sm font-semibold text-n-slate-12">
        {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.TITLE') }}
      </h2>
      <div class="flex items-center gap-2">
        <select
          v-model="selectedId"
          :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.FORM_SELECT')"
          class="shrink-0 px-2 py-1 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-xs max-w-[14rem]"
        >
          <option v-for="f in forms" :key="f.id" :value="f.id">
            {{ f.name || $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.FORM_FALLBACK', { id: f.id }) }}
          </option>
        </select>
        <select
          v-model="period"
          :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.PERIOD')"
          class="shrink-0 px-2 py-1 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-xs"
        >
          <option v-for="days in PERIODS" :key="days" :value="days">
            {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.DAYS', { days }) }}
          </option>
        </select>
        <Button
          :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.EXPORT')"
          size="xs"
          variant="faded"
          color="slate"
          icon="i-ri-download-line"
          :is-loading="exporting"
          :disabled="!leads.length"
          @click="exportCsv"
        />
      </div>
    </div>

    <p v-if="!hasForms" class="text-xs text-n-slate-10">
      {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.NO_FORMS') }}
    </p>

    <div v-else-if="loading" class="flex justify-center py-8 text-n-slate-11">
      <Spinner />
    </div>

    <template v-else>
      <!-- Cards de dashboard -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div class="p-3 rounded-xl border border-n-weak bg-n-alpha-1">
          <span class="text-xs text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.TOTAL') }}</span>
          <p class="text-xl font-extrabold text-n-slate-12">
            {{ totals.total || 0 }}
          </p>
        </div>
        <div class="p-3 rounded-xl border border-n-weak bg-n-alpha-1">
          <span class="text-xs text-n-slate-11">{{
            $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.QUALIFIED')
          }}</span>
          <p class="text-xl font-extrabold text-n-teal-11">
            {{ totals.qualified || 0 }}
            <span class="text-xs font-normal text-n-slate-10"
              >· {{ pct(totals.qualification_rate) }}</span
            >
          </p>
        </div>
        <div class="p-3 rounded-xl border border-n-weak bg-n-alpha-1">
          <span class="text-xs text-n-slate-11">{{
            $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.DISQUALIFIED')
          }}</span>
          <p class="text-xl font-extrabold text-n-ruby-11">
            {{ totals.disqualified || 0 }}
          </p>
        </div>
        <div class="p-3 rounded-xl border border-n-weak bg-n-alpha-1">
          <span class="text-xs text-n-slate-11">{{
            $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.REPLIED')
          }}</span>
          <p class="text-xl font-extrabold text-n-slate-12">
            {{ totals.responded || 0 }}
            <span class="text-xs font-normal text-n-slate-10"
              >· {{ pct(totals.response_rate) }}</span
            >
          </p>
        </div>
      </div>

      <!-- Gráficos -->
      <div
        v-if="totals.total"
        class="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 rounded-xl border border-n-weak"
      >
        <!-- Donut -->
        <div class="flex items-center gap-3">
          <svg viewBox="0 0 130 130" class="size-28 shrink-0">
            <circle
              cx="65"
              cy="65"
              :r="R"
              fill="none"
              stroke="currentColor"
              class="text-n-alpha-2"
              stroke-width="16"
            />
            <circle
              v-for="(seg, i) in donut"
              :key="i"
              cx="65"
              cy="65"
              :r="R"
              fill="none"
              :stroke="seg.color"
              stroke-width="16"
              :stroke-dasharray="seg.dash"
              :stroke-dashoffset="seg.offset"
              transform="rotate(-90 65 65)"
            />
            <text
              x="65"
              y="60"
              text-anchor="middle"
              class="fill-n-slate-12 text-[18px] font-semibold"
            >
              {{ totals.total }}
            </text>
            <text
              x="65"
              y="78"
              text-anchor="middle"
              class="fill-n-slate-10 text-[9px]"
            >
              {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.DONUT_UNIT') }}
            </text>
          </svg>
          <div class="flex flex-col gap-1 text-xs">
            <span class="flex items-center gap-1.5 text-n-slate-11">
              <span class="size-2.5 rounded-full bg-[#22c55e]" />
              {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.LEGEND_QUALIFIED', { count: totals.qualified || 0 }) }}
            </span>
            <span class="flex items-center gap-1.5 text-n-slate-11">
              <span class="size-2.5 rounded-full bg-[#ef4444]" />
              {{
                $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.LEGEND_DISQUALIFIED', {
                  count: totals.disqualified || 0,
                })
              }}
            </span>
            <span class="flex items-center gap-1.5 text-n-slate-11">
              <span class="size-2.5 rounded-full bg-[#94a3b8]" />
              {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.LEGEND_PENDING', { count: totals.pending || 0 }) }}
            </span>
          </div>
        </div>

        <!-- Barras por dia -->
        <div class="sm:col-span-2 flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.PER_DAY') }}</span>
          <div
            v-if="byDay.length"
            class="flex items-end gap-1 h-24 overflow-x-auto"
          >
            <div
              v-for="d in byDay"
              :key="d.date"
              class="flex flex-col items-center gap-1 shrink-0"
              :title="
                $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.DAY_TITLE', {
                  date: d.date,
                  total: d.total,
                  qualified: d.qualified,
                })
              "
            >
              <div
                class="w-4 rounded-t bg-n-alpha-3 flex flex-col-reverse overflow-hidden"
                :style="{ height: `${(d.total / maxDay) * 72 + 4}px` }"
              >
                <div
                  class="w-full bg-[#22c55e]"
                  :style="{ height: `${(d.qualified / (d.total || 1)) * 100}%` }"
                />
              </div>
              <span class="text-[9px] text-n-slate-10 whitespace-nowrap">{{
                formatDay(d.date)
              }}</span>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10 py-2">
            {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.NO_DATA') }}
          </p>
        </div>
      </div>

      <!-- Busca + filtro -->
      <div class="flex items-center gap-2 flex-wrap">
        <div class="relative flex-1 min-w-[12rem]">
          <span
            aria-hidden="true"
            class="i-ph-magnifying-glass pointer-events-none absolute top-1/2 size-3.5 -translate-y-1/2 text-n-slate-10 ltr:left-2 rtl:right-2"
          />
          <input
            v-model="search"
            type="text"
            :placeholder="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.SEARCH')"
            :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.SEARCH')"
            class="reset-base !m-0 h-10 w-full py-1.5 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-xs ltr:!pl-7 ltr:!pr-2 rtl:!pr-7 rtl:!pl-2"
          />
        </div>
        <select
          v-model="statusFilter"
          :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.STATUS_FILTER')"
          class="shrink-0 px-2 py-1.5 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-xs"
        >
          <option v-for="o in STATUS_OPTIONS" :key="o.value" :value="o.value">
            {{ o.label }}
          </option>
        </select>
      </div>

      <!-- Tabela -->
      <div
        v-if="filteredLeads.length"
        class="rounded-xl border border-n-weak overflow-hidden"
      >
        <table class="w-full text-sm">
          <thead class="bg-n-alpha-1 text-n-slate-11">
            <tr>
              <th class="text-left font-medium px-3 py-2">
                {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.COLUMN_CONTACT') }}
              </th>
              <th class="text-left font-medium px-3 py-2 hidden sm:table-cell">
                {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.COLUMN_PHONE') }}
              </th>
              <th class="text-left font-medium px-3 py-2">
                {{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.COLUMN_STATUS') }}
              </th>
              <th class="text-right font-medium px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            <template v-for="l in filteredLeads" :key="l.contact_id">
              <tr
                class="border-t border-n-weak cursor-pointer hover:bg-n-alpha-1"
                @click="toggle(l.contact_id)"
              >
                <td class="px-3 py-2">
                  <div class="flex items-center gap-1.5">
                    <span
                      class="i-ri-arrow-right-s-line size-3.5 text-n-slate-10 transition-transform"
                      :class="{ 'rotate-90': expandedId === l.contact_id }"
                    />
                    <div class="flex flex-col min-w-0">
                      <span class="text-n-slate-12 truncate">{{
                        l.name || EMPTY
                      }}</span>
                      <span class="text-xs text-n-slate-10 sm:hidden">{{
                        l.phone
                      }}</span>
                    </div>
                  </div>
                </td>
                <td class="px-3 py-2 text-n-slate-11 hidden sm:table-cell">
                  {{ l.phone || EMPTY }}
                </td>
                <td class="px-3 py-2">
                  <span
                    class="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium"
                    :class="qualBadge(l.qualified).cls"
                  >
                    {{ qualBadge(l.qualified).label }}
                  </span>
                  <span
                    v-if="l.responded"
                    class="ml-1 i-ri-chat-follow-up-line size-3.5 text-n-teal-10 align-text-bottom"
                    :title="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.RESPONDED')"
                    :aria-label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.RESPONDED')"
                    role="img"
                  />
                </td>
                <td class="px-3 py-2 text-right">
                  <Button
                    :label="$t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.OPEN_CONVERSATION')"
                    size="xs"
                    variant="faded"
                    color="slate"
                    icon="i-ri-external-link-line"
                    @click.stop="openConversation(l)"
                  />
                </td>
              </tr>
              <tr v-if="expandedId === l.contact_id" class="bg-n-alpha-1">
                <td colspan="4" class="px-3 py-3">
                  <div class="flex flex-col gap-3 text-xs">
                    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div>
                        <span class="text-n-slate-10">{{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.EMAIL') }}</span>
                        <p class="text-n-slate-12 break-all">
                          {{ l.email || EMPTY }}
                        </p>
                      </div>
                      <div>
                        <span class="text-n-slate-10">{{
                          $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.RECEIVED_AT')
                        }}</span>
                        <p class="text-n-slate-12">
                          {{ new Date(l.created_at).toLocaleString('pt-BR') }}
                        </p>
                      </div>
                      <div>
                        <span class="text-n-slate-10">{{
                          $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.CONVERSATION')
                        }}</span>
                        <p class="text-n-slate-12">
                          #{{ l.conversation?.display_id || EMPTY }} ·
                          {{ l.conversation?.status || EMPTY }}
                        </p>
                      </div>
                      <div>
                        <span class="text-n-slate-10">{{
                          $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.ASSIGNEE')
                        }}</span>
                        <p class="text-n-slate-12">
                          {{
                            l.conversation?.assignee || $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.UNASSIGNED')
                          }}
                        </p>
                      </div>
                    </div>

                    <div v-if="answerEntries(l).length">
                      <span class="text-n-slate-10">{{
                        $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.ANSWERS')
                      }}</span>
                      <div class="mt-1 flex flex-col gap-0.5">
                        <div
                          v-for="[k, v] in answerEntries(l)"
                          :key="k"
                          class="flex gap-2"
                        >
                          <span class="text-n-slate-11 font-medium">{{ k }}:</span>
                          <span class="text-n-slate-12">{{ v }}</span>
                        </div>
                      </div>
                    </div>

                    <div v-if="l.ai_message">
                      <span class="text-n-slate-10"
                        >{{ $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.SENT_MESSAGE') }}</span
                      >
                      <p
                        class="mt-1 p-2 rounded bg-n-background border border-n-weak text-n-slate-12 whitespace-pre-line"
                      >
                        {{ l.ai_message }}
                      </p>
                    </div>

                    <div v-if="l.labels?.length" class="flex flex-wrap gap-1">
                      <span
                        v-for="lb in l.labels"
                        :key="lb"
                        class="px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-11 text-[11px]"
                      >
                        {{ lb }}
                      </span>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <p v-else class="text-xs text-n-slate-10 py-2">
        {{
          search || statusFilter !== 'all'
            ? $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.EMPTY_FILTERED')
            : $t('INTEGRATION_SETTINGS.META_LEAD_FORMS_PAGE.LEADS.EMPTY_PERIOD')
        }}
      </p>
    </template>
  </section>
</template>
